import React from "react";
import PropTypes from "prop-types";
import { ContentDropdown } from "../../ContentDropdown";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { Question } from "../Components/Question";
import { MainTitle } from "../Components/MainTitle";
import { QuestionImage } from "../Components/QuestionImage";
import { Utilities } from "@learningblocks/exp-constants";
import { Option } from "./Option";
import { SliderGroup } from "../Components/SliderGroup";
import { DataTable } from "@learningblocks/shared-components";
import { Footer } from "../Components/Footer";
import { SubmitButton } from "../Components/SubmitButton";
import { Button } from "@labxchange/buttons";
import { TotalAttempts } from "../Components/TotalAttempts";
import { QuestionFeedback } from "../Components/QuestionFeedback";
import { QuestionVideo } from "../Components/QuestionVideo";
import { useIntl } from "react-intl";
import { NotebookImage } from "@learningblocks/simulations/shared-components";
import "./style.scss";

const { QUESTION_CONTENTS } = Utilities;

/**
 * SingleSelect [CSCQ]:
 *  - You will have n choices to select from
 *  - The question will have a single correct Answer.
 */
export function SingleSelect(props) {
  const {
    isPredictionQuestion = false,
    overrideTitle,
    allowReset = true,
  } = props;
  const intl = useIntl();

  const checkCSCQHasUnsubmittedOptions = (optionList) => {
    const hasUnsubmittedOption = optionList.reduce((boolVal, option) => {
      if (option.disabled === false && option.selected === true) {
        boolVal = true;
      }
      return boolVal;
    }, false);
    return hasUnsubmittedOption === false;
  };

  const setSubmitDisabled = () => {
    if (props.complete) return true;
    else {
      let optionList = [];
      if (props.options) optionList = props.options;
      if (props.typedOptions) optionList = props.typedOptions;
      return checkCSCQHasUnsubmittedOptions(optionList);
    }
  };

  const selectOption = (params) =>
    props.selectOption({ ...params, componentID: props.id });

  const onSubmit = () => props.submitAnswer(props.id, props.type);

  const onReset = () => props.resetAnswer(props.id, props.type);

  const renderQuestion = (key, question) => (
    <Question
      tabIndex={"0"}
      key={key}
      text={question}
      className="cscq__question"
    />
  );

  const renderText = (key, text) => (
    <Question tabIndex={"0"} key={key} text={text} className="cscq__content" />
  );

  const renderDataTable = (key, data) => (
    <DataTable
      key={key}
      className={`questionTable__tableWrapper ${props.dataTable.className}`}
      indices={data.indices}
      data={data.data}
      onCellChange={props.onCellChange}
    />
  );

  const renderImage = (key, image) => (
    <QuestionImage
      tabIndex={"0"}
      key={key}
      altText={props.altText}
      image={image}
      className="cscq__image"
      preview={props.preview}
    />
  );
  const renderVideo = (key, video) => (
    <QuestionVideo
      tabIndex={"0"}
      key={key}
      //captions={video.captions}
      video={video.src}
      className="cscq__video"
      preview={props.preview}
    />
  );

  const figcaption = (key, content) => {
    return (
      <div style={{ marginBottom: "16px" }}>
        <NotebookImage
          key={key}
          {...{ image: content }}
          getGlossaryItem={props.getGlossaryItem}
          openGlossary={props.openGlossary}
        />
      </div>
    );
  };

  const getIsCorrect = ({ id }) => {
    return props.answerId.includes(id);
  };

  const renderOptions = (key, options) => {
    return (
      <>
        {options.map((option, id) => {
          const index = `${key}_${id}`;
          return (
            <Option
              key={index}
              {...option}
              index={id}
              tabIndex={"0"}
              selectOption={selectOption}
              isCorrect={getIsCorrect(option)}
              isPredictionQuestion={isPredictionQuestion}
              showFeedback={props.showFeedback}
              showFeedbackByDefault={!props.onShowFeedback}
            />
          );
        })}
        {isPredictionQuestion && (
          <QuestionFeedback
            feedback={props.feedback}
            isPredictionQuestion={isPredictionQuestion}
            key={key}
            tabIndex={"0"}
            answerId={props.answerId}
            checkboxes={props.options}
            className="mscq__answerfeedback"
          />
        )}
      </>
    );
  };

  const renderSliderGroups = (key, sliderGroups) => {
    return sliderGroups.map((slider, id) => (
      <SliderGroup key={`${key}_${id}`} {...slider} />
    ));
  };

  const hintDetailsSection = (key, hintDetails) => {
    return <ContentDropdown key={key} contentDetails={hintDetails} />;
  };

  const renderByType = (type, key, content) => {
    switch (type) {
      case QUESTION_CONTENTS.QUESTION:
        return renderQuestion(key, content);
      case QUESTION_CONTENTS.TEXT:
        return renderText(key, content);
      case QUESTION_CONTENTS.IMAGE:
        return renderImage(key, content);
      case QUESTION_CONTENTS.VIDEO:
        return renderVideo(key, content);
      case QUESTION_CONTENTS.FIGCAPTION:
        return figcaption(key, content);
      case QUESTION_CONTENTS.OPTIONS:
      case QUESTION_CONTENTS.TYPED_OPTIONS:
        return renderOptions(key, content);
      case QUESTION_CONTENTS.SLIDER_GROUPS:
        return renderSliderGroups(key, content);
      case QUESTION_CONTENTS.HINTS:
        return hintDetailsSection(key, props.hintDetails);
      case QUESTION_CONTENTS.CONTENT_LIST:
        return content.map((listItem, subKey) =>
          renderByType(listItem.type, `${key}_${subKey}`, listItem.content)
        );
      case QUESTION_CONTENTS.DATA_TABLE:
        return renderDataTable(key, content);
      default:
        return null;
    }
  };

  const wrapperClass = ` cscq ${props.className}`;
  const resetButtonText = {
    id: "Reset options",
    defaultMessage: "Reset options",
    description: "",
  };
  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle
        title={intl.formatMessage({
          id: isPredictionQuestion
            ? overrideTitle
              ? overrideTitle
              : "Prediction Question"
            : "SINGLE-SELECT QUESTION",
          defaultMessage: isPredictionQuestion
            ? overrideTitle
              ? overrideTitle
              : "Prediction Question"
            : "SINGLE-SELECT QUESTION",
          description: "",
        })}
        className="cscq__mainTitle"
      />
      {props.content.map((type, key) => renderByType(type, key, props[type]))}
      <Footer className="cscq__footer">
        {props.showAttempts && (
          <TotalAttempts
            totalAttempts={props.totalAttempts}
            isPredictionQuestion={isPredictionQuestion}
            className="cscq__footer__totalAttempts"
          />
        )}
        {props.complete && isPredictionQuestion && allowReset ? (
          <Button
            className="secondary-button"
            onClick={onReset}
            titleDescriptor={resetButtonText}
            iconName={null}
          />
        ) : props.totalAttempts === 0 && props.onShowFeedback ? (
          <SubmitButton
            label={intl.formatMessage({
              id: "feedback",
              defaultMessage: "See all feedback",
              description: "",
            })}
            className="cscq__all_feedback"
            disabled={props.showFeedback}
            onSubmit={props.onShowFeedback}
          />
        ) : (
          <SubmitButton
            label={intl.formatMessage({
              id: "Submit answer",
              defaultMessage: "Submit answer",
              description: "",
            })}
            onSubmit={onSubmit}
            disabled={setSubmitDisabled()}
          />
        )}
      </Footer>
    </QuestionWrapper>
  );
}

SingleSelect.propTypes = {
  /**
   * The prop for the ID of correct answer option [ -1 means all answers are correct]
   */
  answerId: PropTypes.arrayOf(PropTypes.number).isRequired,
  /**
   * The custom classname you wanna inject on the root of the component
   */
  className: PropTypes.string,
  /**
   * The prop to set if the component is complete
   */
  complete: PropTypes.bool.isRequired,
  /**
   * The prop to set if the component isPredictionQuestion
   */
  isPredictionQuestion: PropTypes.bool,
  /**
   * List of static contents of type question, text, and image you want to display
   */
  contentList: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Type of content Item
       */
      type: PropTypes.oneOf(["question", "image", "text"]),
      /**
       * Content Item assicated with the type
       */
      content: PropTypes.string,
    })
  ),
  /**
   * The prop to specify all contents in the question
   */
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * The prop that hold the unique id for the component
   */
  id: PropTypes.number,
  /**
   * The prop that hold image data
   */
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * The prop that hold the array of answer options.
   */
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The prop that holds the text for the question
   */
  question: PropTypes.oneOfType([PropTypes.string, PropTypes.object])
    .isRequired,
  /**
   * The prop that holds the total attempts,
   */
  totalAttempts: PropTypes.number.isRequired,
  /**
   * The prop that holds the Component type.
   */
  type: PropTypes.oneOf(["CSCQ_COLOR", "CSCQ", "CSCQ_NEW"]),
  /**
   * The callback prop executed when submit is clicked
   */
  submitAnswer: PropTypes.func.isRequired,
  /**
   * The callback prop executed when submit is clicked
   */
  resetAnswer: PropTypes.func.isRequired,
  /**
   * The callback to be executed when an option is selected
   */
  selectOption: PropTypes.func.isRequired,
  /**
   * The content that is shown in the hint sectoin
   */
  hintDetails: PropTypes.shape({
    /**
     * The label that is added to the hint section. Usually Hint
     */
    label: PropTypes.string.isRequired,
    /**
     * Description provided for the hint section
     */
    description: PropTypes.string.isRequired,
    /**
     * To show the question title
     */
    title: PropTypes.string,
    /**
     * className to be set for CSCQColor
     */
    className: PropTypes.string,
  }),
  /**
   * list of sliders you wanna display on group
   */
  sliderGroups: PropTypes.shape({
    /**
     * The prop that hold the unique id for the component
     */
    id: PropTypes.number.isRequired,
    /**
     * Label for the slider
     */
    label: PropTypes.number,
    /**
     * list of sliders you wanna display on group
     */
    sliders: PropTypes.array,
    preview: PropTypes.bool,
  }),
  /**
   * Submit Button Label
   */
  submitLabel: PropTypes.string,
  /**
   * show feedback
   */
  showFeedback: PropTypes.bool,
  /**
   * on show feedback
   */
  onShowFeedback: PropTypes.func,
  /**
   * Custom feedback messages for COMPLETE and INCOMPLETE states
   */
  feedback: PropTypes.shape({
    SUCCESS_MESSAGE: PropTypes.string || PropTypes.object,
    FAIL_MESSAGE: PropTypes.string || PropTypes.object,
  }),
};

SingleSelect.defaultProps = {
  className: "",
  type: "CSCQ",
  answerId: 0,
  complete: false,
  content: [],
  image: "",
  options: [],
  question: "",
  totalAttempts: 0,
  showAttempts: false,
  caption: "",
  preview: false,
  submitLabel: "Submit answer",
  feedback: null,
};
